import React from 'react';
import { SOCIAL_PLATFORMS, useSnackbar } from 'ui-components';
import { ContentCardPlatformIcon } from '~/components/ContentCard/components/ContentCardPlatformIcon';
import { IconBox } from '~/components/IconBox';
import {
  ALL_SOCIAL_PLATFORM_TYPE,
  MONTH_NAME_DAY_YEAR_FORMAT,
} from '~/constants';
import { trackEvent } from '~/core/utils/trackEvent';
import {
  IApiSearchContent,
  IApiSearchTiktokContent,
} from '~/searchApi/content';
import { formatDate } from '~/utils';

interface ContentFooterProps {
  contentData?: IApiSearchContent;
}

export const ContentFooter: React.FC<ContentFooterProps> = ({
  contentData,
}) => {
  const snackbar = useSnackbar();

  const generateShareUrl = (contentData: IApiSearchContent): string | null => {
    if (contentData.platform === SOCIAL_PLATFORMS.tiktok) {
      const tiktokContent = contentData as IApiSearchTiktokContent;
      return tiktokContent.embedUrl || null;
    }
    if (contentData.platform === SOCIAL_PLATFORMS.youtube) {
      return contentData.networkId
        ? `https://www.youtube.com/watch?v=${contentData.networkId}`
        : null;
    }
    if (contentData.platform === SOCIAL_PLATFORMS.instagram) {
      return contentData.shareUrl || null;
    }
    return null;
  };

  const handleShareLinkClick = () => {
    if (!contentData) return;

    // Track share link clicks
    trackEvent('wm-tta-content-share-link-click', {
      platform: contentData.platform || 'unknown',
      contentId: contentData.id || 'unknown',
      shareUrl: generateShareUrl(contentData) || 'unknown',
      hasEngagements: (contentData.engagements ?? 0) > 0,
    });
  };

  const handleIrrelevantPostClick = () => {
    // Track irrelevant post feedback
    trackEvent('wm-tta-irrelevant-post-feedback', {
      platform: contentData?.platform || 'unknown',
      contentId: contentData?.id || 'unknown',
      publishedAt:
        contentData?.publishedAt || contentData?.updatedAt || 'unknown',
    });

    snackbar.success({
      description: 'Suggestion submitted!',
      duration: 3000,
      isCloseable: true,
    });
  };

  if (!contentData) {
    return null;
  }

  const shareUrl = generateShareUrl(contentData);

  return (
    <div className="mt-4 flex items-center justify-between">
      {shareUrl ? (
        <a
          href={shareUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 hover:opacity-80"
          onClick={handleShareLinkClick}
        >
          {contentData.platform && (
            <IconBox
              icon={
                <ContentCardPlatformIcon
                  boxSize={4}
                  platform={contentData.platform as ALL_SOCIAL_PLATFORM_TYPE}
                />
              }
              width={6}
              height={6}
            />
          )}
          <span className="text-12 font-400 text-neutral-400">
            Posted{' '}
            {contentData.publishedAt
              ? formatDate(
                  new Date(contentData.publishedAt),
                  MONTH_NAME_DAY_YEAR_FORMAT,
                )
              : contentData.updatedAt
                ? formatDate(
                    new Date(contentData.updatedAt),
                    MONTH_NAME_DAY_YEAR_FORMAT,
                  )
                : 'Unknown date'}
          </span>
        </a>
      ) : (
        <div className="flex items-center gap-2">
          {contentData.platform && (
            <IconBox
              icon={
                <ContentCardPlatformIcon
                  boxSize={4}
                  platform={contentData.platform as ALL_SOCIAL_PLATFORM_TYPE}
                />
              }
              width={6}
              height={6}
            />
          )}
          <span className="text-12 font-400 text-neutral-400">
            Posted{' '}
            {contentData.publishedAt
              ? formatDate(
                  new Date(contentData.publishedAt),
                  MONTH_NAME_DAY_YEAR_FORMAT,
                )
              : contentData.updatedAt
                ? formatDate(
                    new Date(contentData.updatedAt),
                    MONTH_NAME_DAY_YEAR_FORMAT,
                  )
                : 'Unknown date'}
          </span>
        </div>
      )}
      <div className="text-12 text-neutral-400">
        Irrelevant post?{' '}
        <button
          onClick={handleIrrelevantPostClick}
          className="cursor-pointer text-link-text-primary-default hover:text-link-text-primary-hover hover:underline"
        >
          Let us know!
        </button>
      </div>
    </div>
  );
};
